/**
 * Shared table tooling — sort + multi-select.
 * Used by Clients, Proposals, Contracts, Invoices, Renewals.
 */
import { useMemo, useState, useCallback } from 'react';

export function useTableTools({ defaultSortKey = null, defaultSortDir = 'asc' } = {}) {
    const [selected, setSelected] = useState(() => new Set());
    const [sortKey, setSortKey] = useState(defaultSortKey);
    const [sortDir, setSortDir] = useState(defaultSortDir);

    const toggleOne = useCallback((id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const toggleAll = useCallback((items) => {
        setSelected(prev => {
            if (prev.size === items.length && items.length > 0) return new Set();
            return new Set(items.map(i => i.id));
        });
    }, []);

    const clear = useCallback(() => setSelected(new Set()), []);

    const changeSort = useCallback((key) => {
        setSortKey(prev => {
            if (prev === key) {
                setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                return key;
            }
            setSortDir('asc');
            return key;
        });
    }, []);

    return { selected, toggleOne, toggleAll, clear, sortKey, sortDir, changeSort, setSelected };
}

/** Apply current sort to a list of rows given a map of key → accessor function. */
export function applySort(items, sortKey, sortDir, accessors) {
    if (!sortKey || !accessors[sortKey]) return items;
    const get = accessors[sortKey];
    const copy = [...items];
    copy.sort((a, b) => {
        const va = get(a);
        const vb = get(b);
        const na = va == null ? '' : va;
        const nb = vb == null ? '' : vb;
        if (typeof na === 'number' && typeof nb === 'number') {
            return sortDir === 'asc' ? na - nb : nb - na;
        }
        const sa = String(na).toLowerCase();
        const sb = String(nb).toLowerCase();
        if (sa < sb) return sortDir === 'asc' ? -1 : 1;
        if (sa > sb) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });
    return copy;
}

/** Memoised helper: useMemo wrapper around applySort for convenience. */
export function useSortedItems(items, sortKey, sortDir, accessors) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => applySort(items, sortKey, sortDir, accessors), [items, sortKey, sortDir]);
}
